import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Star, MapPin, CheckCircle2, ShieldCheck } from "lucide-react";
import { services, projects, stats, reviews, locations, process } from "../data/mock";
import { openContactModal } from "../lib/contactBus";

function useInView(ref) {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setSeen(true); }, { threshold: 0.25 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return seen;
}

export default function Home() {
  const statRef = useRef(null);
  const seen = useInView(statRef);

  return (
    <>
      {/* ========== HERO BENTO ========== */}
      <section className="max-w-[1480px] mx-auto px-6 lg:px-10 pt-10 pb-12">
        <div className="grid grid-cols-12 gap-3 lg:gap-4">
          {/* Big headline block */}
          <div className="col-span-12 lg:col-span-8 bento-card p-8 lg:p-12 relative overflow-hidden min-h-[480px] flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3">
                <span className="section-tag">// EDGE DEVELOPER, LLC</span>
                <span className="w-12 h-px bg-[var(--terracotta)]" />
              </div>
              <h1 className="font-display text-[68px] sm:text-[96px] lg:text-[140px] uppercase leading-[0.9] mt-6 tracking-tight">
                We build<br />
                <span className="text-[var(--terracotta)]">homes</span> that<br />
                outlast<br />
                <span className="italic font-narrow text-[var(--slate-500)] text-[42px] sm:text-[56px] lg:text-[72px] font-light">/ trends.</span>
              </h1>
            </div>
            <div className="flex flex-wrap items-end justify-between gap-6 mt-10">
              <p className="max-w-md text-[14px] leading-7 text-[var(--slate-700)]">
                Full-service home renovation contractors based in North Mankato, MN — proudly serving Mankato, St. Peter, Lake Crystal, Eagle Lake, and the surrounding southern Minnesota region. One team. One budget. Zero surprises.
              </p>
              <div className="flex gap-3">
                <button onClick={openContactModal} className="btn-terracotta">Start Project <ArrowRight size={15} /></button>
                <Link to="/projects" className="btn-outline">See Work</Link>
              </div>
            </div>
          </div>

          {/* Hero image block */}
          <div className="col-span-12 lg:col-span-4 relative min-h-[480px] overflow-hidden bg-[var(--slate-900)] corner-frame group">
            <img src="https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=1200&q=80" alt="Construction site" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--slate-950)] via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-[var(--off-white)]">
              <p className="font-mono-sans text-[10px] text-[var(--terracotta-light)]">// CURRENT BUILD</p>
              <p className="font-display text-[28px] uppercase leading-tight mt-1">Lakeside Retreat</p>
              <p className="text-[12px] text-[var(--slate-300)] mt-1">Lake Crystal, MN — 3,400 sq ft</p>
            </div>
          </div>

          {/* Trust row */}
          <div className="col-span-6 lg:col-span-3 bento-card dark p-6 flex flex-col justify-between min-h-[180px]">
            <ShieldCheck size={28} className="text-[var(--terracotta)]" strokeWidth={1.5} />
            <div>
              <p className="font-display text-[44px] leading-none">12</p>
              <p className="font-mono-sans text-[11px] text-[var(--slate-300)] mt-1">YEARS LICENSED</p>
            </div>
          </div>
          <div className="col-span-6 lg:col-span-3 bento-card p-6 flex flex-col justify-between min-h-[180px]">
            <CheckCircle2 size={28} className="text-[var(--terracotta)]" strokeWidth={1.5} />
            <div>
              <p className="font-display text-[44px] leading-none">748+</p>
              <p className="font-mono-sans text-[11px] text-[var(--slate-500)] mt-1">HOMES DELIVERED</p>
            </div>
          </div>
          <div className="col-span-6 lg:col-span-3 bento-card terracotta p-6 flex flex-col justify-between min-h-[180px]">
            <Star size={28} fill="currentColor" strokeWidth={1.5} />
            <div>
              <p className="font-display text-[44px] leading-none">5.0</p>
              <p className="font-mono-sans text-[11px] opacity-80 mt-1">RATING / 1,022 REVIEWS</p>
            </div>
          </div>
          <div className="col-span-6 lg:col-span-3 bento-card p-6 flex flex-col justify-between min-h-[180px]">
            <MapPin size={28} className="text-[var(--terracotta)]" strokeWidth={1.5} />
            <div>
              <p className="font-display text-[44px] leading-none">MN</p>
              <p className="font-mono-sans text-[11px] text-[var(--slate-500)] mt-1">SOUTHERN MINNESOTA</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SERVICES BENTO ========== */}
      <section className="max-w-[1480px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="section-tag mb-3">// 01 — WHAT WE BUILD</p>
            <h2 className="font-display text-[48px] sm:text-[64px] lg:text-[80px] uppercase leading-[0.95]">Services<br /><span className="text-[var(--terracotta)]">on the table.</span></h2>
          </div>
          <Link to="/services" className="link-tick font-mono-sans text-[12px] font-semibold tracking-widest text-[var(--slate-900)]">
            ALL SERVICES <ArrowUpRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-12 gap-3 lg:gap-4">
          {services.slice(0, 6).map((s, i) => {
            const span = i === 0 ? "col-span-12 lg:col-span-6 row-span-2" : (i === 3 ? "col-span-12 lg:col-span-6" : "col-span-6 lg:col-span-3");
            const minH = i === 0 ? "min-h-[520px]" : "min-h-[250px]";
            return (
              <div key={s.id} className={`bento-card relative overflow-hidden group ${span} ${minH}`}>
                <img src={s.image} alt={s.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--slate-950)]/85 via-[var(--slate-950)]/30 to-transparent" />
                <div className="relative h-full flex flex-col justify-between p-5 lg:p-6 text-[var(--off-white)]">
                  <div className="flex items-center justify-between">
                    <span className="font-mono-sans text-[10px] text-[var(--terracotta-light)]">// {s.id}</span>
                    <span className="font-mono-sans text-[9.5px] bg-[var(--terracotta)] px-2 py-1">{s.tag}</span>
                  </div>
                  <div>
                    <h3 className={`font-display uppercase leading-[0.95] ${i === 0 ? "text-[44px] lg:text-[60px]" : "text-[24px] lg:text-[30px]"}`}>{s.title}</h3>
                    <p className="text-[12.5px] text-[var(--slate-300)] mt-2 max-w-md">{s.body}</p>
                    <ArrowUpRight size={20} className="mt-3 text-[var(--off-white)] group-hover:text-[var(--terracotta-light)] group-hover:translate-x-1 -translate-y-0 group-hover:-translate-y-1 transition-all" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ========== PROCESS RAIL ========== */}
      <section className="bg-[var(--slate-950)] text-[var(--off-white)] py-20 lg:py-28">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <p className="section-tag !text-[var(--terracotta-light)] mb-3">// 02 — PROCESS</p>
              <h2 className="font-display text-[48px] sm:text-[64px] lg:text-[80px] uppercase leading-[0.95]">From blueprint<br /><span className="text-[var(--terracotta)]">to keys.</span></h2>
            </div>
            <p className="max-w-md text-[14px] text-[var(--slate-300)] leading-7">
              Five accountable phases. One team that owns every one of them. The plan you sign is the plan we build.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 border-t border-[var(--slate-700)]">
            {process.map((p) => (
              <div key={p.num} className="border-b md:border-b-0 md:border-r last:border-r-0 border-[var(--slate-700)] p-6 hover:bg-[var(--slate-900)] transition-colors group">
                <p className="font-display text-[64px] text-[var(--terracotta)] group-hover:text-[var(--off-white)] transition-colors">{p.num}</p>
                <h3 className="font-display text-[24px] uppercase mt-2">{p.title}</h3>
                <p className="text-[12.5px] text-[var(--slate-300)] mt-3 leading-6">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== STATS STRIP ========== */}
      <section ref={statRef} className="max-w-[1480px] mx-auto px-6 lg:px-10 py-20">
        <p className="section-tag mb-3">// 03 — NUMBERS</p>
        <h2 className="font-display text-[48px] sm:text-[64px] uppercase leading-[0.95] mb-12">Receipts, not <span className="text-[var(--terracotta)]">promises.</span></h2>
        <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border-t border-l border-[var(--slate-300)] ${seen ? "fade-up" : ""}`}>
          {stats.map((s) => (
            <div key={s.label} className="border-b border-r border-[var(--slate-300)] p-6 lg:p-8 hover:bg-[var(--bg-secondary)] transition-colors">
              <p className="font-display text-[44px] lg:text-[56px] text-[var(--slate-900)] leading-none">{s.value}</p>
              <p className="font-mono-sans text-[10.5px] text-[var(--slate-500)] mt-3">{s.label.toUpperCase()}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ========== FEATURED PROJECTS ========== */}
      <section className="max-w-[1480px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="section-tag mb-3">// 04 — SELECTED WORK</p>
            <h2 className="font-display text-[48px] sm:text-[64px] lg:text-[80px] uppercase leading-[0.95]">Recent<br /><span className="text-[var(--terracotta)]">builds.</span></h2>
          </div>
          <Link to="/projects" className="link-tick font-mono-sans text-[12px] font-semibold tracking-widest text-[var(--slate-900)]">
            ALL PROJECTS <ArrowUpRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-12 gap-3 lg:gap-4">
          {projects.slice(0, 4).map((p, i) => (
            <Link key={p.slug} to="/projects" className={`bento-card relative overflow-hidden group ${i === 0 || i === 3 ? "col-span-12 lg:col-span-7" : "col-span-12 lg:col-span-5"} min-h-[380px]`}>
              <img src={p.image} alt={p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--slate-950)]/85 via-transparent to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-between p-6 text-[var(--off-white)]">
                <div className="flex justify-between items-start">
                  <span className="font-mono-sans text-[10px] bg-[var(--slate-900)] px-2 py-1">{p.year}</span>
                  <span className="font-mono-sans text-[10px] text-[var(--terracotta-light)]">{p.type.toUpperCase()}</span>
                </div>
                <div>
                  <p className="font-mono-sans text-[10px] text-[var(--terracotta-light)] mb-1">// {p.location.toUpperCase()}</p>
                  <h3 className="font-display text-[34px] sm:text-[44px] uppercase leading-[0.95]">{p.title}</h3>
                  <p className="text-[12px] text-[var(--slate-300)] mt-1">{p.subtitle} — {p.sqft} sq ft</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ========== LOCATION STRIP ========== */}
      <section className="bg-[var(--bg-secondary)] py-20">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <p className="section-tag mb-3">// 05 — WHERE WE WORK</p>
              <h2 className="font-display text-[48px] sm:text-[64px] uppercase leading-[0.95]">One shop.<br /><span className="text-[var(--terracotta)]">One standard.</span></h2>
            </div>
            <Link to="/locations" className="link-tick font-mono-sans text-[12px] font-semibold tracking-widest text-[var(--slate-900)]">
              VIEW LOCATION <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-12 gap-3 lg:gap-4">
            {locations.map((l) => (
              <Link key={l.slug} to="/locations" className="bento-card relative overflow-hidden group min-h-[420px] block col-span-12 lg:col-span-8">
                <img src={l.image} alt={l.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--slate-950)]/90 via-[var(--slate-950)]/30 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-between p-6 text-[var(--off-white)]">
                  <span className="font-mono-sans text-[10px] bg-[var(--terracotta)] px-2 py-1 self-start">{l.region}</span>
                  <div>
                    <h3 className="font-display text-[44px] sm:text-[56px] uppercase leading-[0.95]">{l.name}</h3>
                    <p className="text-[12.5px] text-[var(--slate-300)] mt-2 max-w-md">{l.coverage}</p>
                  </div>
                </div>
              </Link>
            ))}
            <div className="bento-card dark p-8 lg:p-10 col-span-12 lg:col-span-4 min-h-[420px] flex flex-col justify-between">
              <div>
                <p className="section-tag !text-[var(--terracotta-light)] mb-3">// HEADQUARTERS</p>
                <h3 className="font-display text-[28px] uppercase leading-tight">743 Centre</h3>
                <p className="text-[14px] text-[var(--slate-300)] mt-1">North Mankato, MN 56003</p>
              </div>
              <ul className="space-y-3 text-[13px] text-[var(--slate-300)]">
                <li className="flex items-center gap-2"><span className="font-mono-sans text-[10px] text-[var(--terracotta-light)] w-16">PHONE</span> (507) 351-8787</li>
                <li className="flex items-center gap-2"><span className="font-mono-sans text-[10px] text-[var(--terracotta-light)] w-16">EMAIL</span> <span className="break-all">info@edge-developerllc.com</span></li>
                <li className="flex items-center gap-2"><span className="font-mono-sans text-[10px] text-[var(--terracotta-light)] w-16">HOURS</span> Mon–Fri, 7am–5pm</li>
              </ul>
              <Link to="/contact" className="btn-terracotta self-start">Schedule Visit <ArrowUpRight size={14} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========== REVIEWS ========== */}
      <section className="max-w-[1480px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="section-tag mb-3">// 06 — FIELD REPORTS</p>
            <h2 className="font-display text-[48px] sm:text-[64px] uppercase leading-[0.95]">What clients<br /><span className="text-[var(--terracotta)]">actually say.</span></h2>
          </div>
          <div className="flex items-center gap-2">
            {[1,2,3,4,5].map((i) => <Star key={i} size={16} fill="var(--terracotta)" stroke="var(--terracotta)" />)}
            <span className="font-mono-sans text-[11px] text-[var(--slate-700)] ml-2">5.0 / 180 REVIEWS</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
          {reviews.slice(0, 6).map((r, i) => (
            <article key={i} className="bento-card p-6 hover-lift">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center font-display text-[18px] text-[var(--off-white)]" style={{ background: r.color }}>{r.initial}</div>
                <div>
                  <p className="font-mono-sans text-[11px] font-semibold text-[var(--slate-900)]">{r.name.toUpperCase()}</p>
                  <p className="text-[11px] text-[var(--slate-500)]">{r.time}</p>
                </div>
                <div className="ml-auto flex gap-0.5">{[...Array(r.rating)].map((_, k) => <Star key={k} size={11} fill="var(--terracotta)" stroke="var(--terracotta)" />)}</div>
              </div>
              <p className="text-[13px] leading-6 text-[var(--slate-700)] mt-4">“{r.text}”</p>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}
